/**
 * Helpers for OncoEMR task create and display formatting.
 * @module oncoemrTaskHelpers
 */

import type { OncoemrThreadMessageSender } from "../data-model/oncoemrTaskMessagesModel";

/** Placeholder when a thread message has no sender name or user id. */
export const ONCOEMR_MESSAGE_SENDER_UNKNOWN = "—";

/**
 * Resolves the label shown above a task thread message and for avatar initials.
 *
 * @param message - Thread row with optional `sender_name` and `sender_user_id`.
 * @return Display name: trimmed `sender_name`, else `sender_user_id`, else "—".
 */
export const resolveMessageSenderDisplayName = (
  message: OncoemrThreadMessageSender,
): string => {
  const name = message.sender_name?.trim();
  if (name) {
    return name;
  }

  const userId = message.sender_user_id?.trim();
  if (userId) {
    return userId;
  }

  return ONCOEMR_MESSAGE_SENDER_UNKNOWN;
};

/**
 * Formats plain-text task notes as HTML for OncoEMR `initial_message`.
 *
 * @param notes - User-entered task notes from the create modal.
 * @returns HTML string (wrapped in `<p>` when input is plain text).
 */
export const formatOncoemrTaskInitialMessage = (notes: string): string => {
  const trimmed = notes.trim();
  if (!trimmed) {
    return "<p></p>";
  }

  if (trimmed.includes("<")) {
    return trimmed;
  }

  const escaped = trimmed
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

  return `<p>${escaped}</p>`;
};

/**
 * Reduces simple OncoEMR HTML bodies to plain text for chat bubbles.
 *
 * @param html - HTML from OncoEMR (e.g. wrapped in `<p>` tags).
 * @returns Collapsed plain text suitable for chat bubble UI components.
 */
export const stripHtmlToPlainText = (html: string): string => {
  if (!html) {
    return "";
  }

  const withoutTags = html.replace(/<[^>]*>/g, " ");
  const decoded = withoutTags
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">");

  return decoded.replace(/\s+/g, " ").trim();
};
